import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Pagination,
  CircularProgress,
  Chip,
  Avatar,
} from '@mui/material';
import {
  Search,
  PlayCircleOutline,
  Download,
  InfoOutlined,
  VideoLibrary,
  CheckCircleOutline,
  HourglassEmpty,
  ErrorOutline,
  MovieOutlined,
} from '@mui/icons-material';
import { api } from '../context/AuthContext';
import RecordingPlayerModal from '../components/RecordingPlayerModal';
import RecordingDetailsModal from '../components/RecordingDetailsModal';
import { computeDurationSeconds, formatDuration, formatFileSize } from '../utils/recordingFormat';

const BRAND = '#1E3A8A';
const PAGE_SIZE = 10;

const STATUS_META = {
  COMPLETED:    { label: 'Completed',    color: '#2e7d32', bg: '#e8f5e9' },
  PROCESSING:   { label: 'Processing',   color: '#ed6c02', bg: '#fff3e0' },
  UPLOADING:    { label: 'Uploading',    color: '#ed6c02', bg: '#fff3e0' },
  RECORDING:    { label: 'Recording',    color: '#0288d1', bg: '#e1f5fe' },
  INITIALIZING: { label: 'Initializing', color: '#0288d1', bg: '#e1f5fe' },
  FAILED:       { label: 'Failed',       color: '#c62828', bg: '#ffebee' },
  INCOMPLETE:   { label: 'Incomplete',   color: '#c62828', bg: '#ffebee' },
};

function StatusChip({ status }) {
  const meta = STATUS_META[status] || { label: status || 'Unknown', color: '#616161', bg: '#f5f5f5' };
  return (
    <Chip
      size="small"
      label={meta.label}
      sx={{ bgcolor: meta.bg, color: meta.color, fontWeight: 600, borderRadius: '6px' }}
    />
  );
}

function SummaryCard({ icon, label, value, color }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        height: '100%',
      }}
    >
      <Box sx={{
        width: 48, height: 48, borderRadius: '10px', bgcolor: `${color}1A`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', color,
      }}>
        {icon}
      </Box>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a1a', lineHeight: 1.2 }}>
          {value}
        </Typography>
        <Typography variant="body2" sx={{ color: '#6b7280' }}>{label}</Typography>
      </Box>
    </Paper>
  );
}

export default function InterviewRecordings() {
  const [recordings, setRecordings] = useState([]);
  const [stats, setStats] = useState({ total: 0, completed: 0, processing: 0, failed: 0 });
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [jobFilter, setJobFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [jobOptions, setJobOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [watchRecordingId, setWatchRecordingId] = useState(null);
  const [detailsRecording, setDetailsRecording] = useState(null);

  useEffect(() => {
    api.get('/jobs/admin').then(res => {
      const jobs = res.data?.jobs || [];
      setJobOptions(jobs.map(j => ({ id: j.id, title: j.title })));
    }).catch(() => { /* filter dropdown is a non-critical enhancement */ });
  }, []);

  const fetchRecordings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, limit: PAGE_SIZE };
      if (search) params.search = search;
      if (jobFilter) params.jobId = jobFilter;
      if (statusFilter) params.status = statusFilter;

      const res = await api.get('/interview-recordings', { params });
      const data = res.data?.data || {};
      setRecordings(data.recordings || []);
      setStats(data.stats || { total: 0, completed: 0, processing: 0, failed: 0 });
      setPagination(data.pagination || { page: 1, totalPages: 1, total: 0 });
    } catch (err) {
      console.error('Failed to fetch interview recordings:', err);
      setError('Unable to load recordings right now. Please try again.');
      setRecordings([]);
    } finally {
      setLoading(false);
    }
  }, [page, search, jobFilter, statusFilter]);

  useEffect(() => {
    const timer = setTimeout(fetchRecordings, 300); // debounce search/filter changes
    return () => clearTimeout(timer);
  }, [fetchRecordings]);

  // Three distinct actions with their own handlers/state — Watch opens only the
  // player, Details opens only the details view, Download never opens either.
  const handleWatch = (recording) => {
    setWatchRecordingId(recording.recording_id);
  };

  const handleDownload = async (recording) => {
    try {
      const res = await api.get(`/interview-recordings/${recording.recording_id}/download-url`);
      const url = res.data?.data?.url;
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Failed to get download URL:', err);
    }
  };

  const handleDetails = (recording) => {
    setDetailsRecording(recording);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: BRAND }}>
          Interview Recordings
        </Typography>
        <Typography variant="body1" sx={{ color: '#6b7280', mt: 0.5 }}>
          Review and monitor recorded candidate interview sessions.
        </Typography>
      </Box>

      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard icon={<VideoLibrary />} label="Total Recordings" value={stats.total} color={BRAND} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard icon={<CheckCircleOutline />} label="Completed" value={stats.completed} color="#2e7d32" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard icon={<HourglassEmpty />} label="Processing" value={stats.processing} color="#ed6c02" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard icon={<ErrorOutline />} label="Failed / Incomplete" value={stats.failed} color="#c62828" />
        </Grid>
      </Grid>

      <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by candidate name or email"
              value={search}
              onChange={(e) => { setPage(1); setSearch(e.target.value); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
            />
          </Grid>
          <Grid item xs={6} md={3.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Job</InputLabel>
              <Select label="Job" value={jobFilter} onChange={(e) => { setPage(1); setJobFilter(e.target.value); }}>
                <MenuItem value="">All Jobs</MenuItem>
                {jobOptions.map(j => (
                  <MenuItem key={j.id} value={j.id}>{j.title}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={3.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={statusFilter} onChange={(e) => { setPage(1); setStatusFilter(e.target.value); }}>
                <MenuItem value="">All Statuses</MenuItem>
                {Object.keys(STATUS_META).map(s => (
                  <MenuItem key={s} value={s}>{STATUS_META[s].label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <ErrorOutline sx={{ fontSize: 48, color: '#c62828', mb: 1 }} />
            <Typography sx={{ color: '#6b7280' }}>{error}</Typography>
          </Box>
        ) : recordings.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <MovieOutlined sx={{ fontSize: 48, color: '#9ca3af', mb: 1 }} />
            <Typography sx={{ color: '#6b7280' }}>No interview recordings found.</Typography>
          </Box>
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f9fafb' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Candidate</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Job Title</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Interview Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Duration</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>File Size</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recordings.map((r) => (
                  <TableRow key={r.recording_id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: BRAND, fontSize: 14 }}>
                          {(r.candidate_name || r.candidate_email || '?').charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.candidate_name || '—'}</Typography>
                          <Typography variant="caption" sx={{ color: '#6b7280' }}>{r.candidate_email || '—'}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{r.job_title || '—'}</TableCell>
                    <TableCell>{r.created_at ? new Date(r.created_at).toLocaleString() : '—'}</TableCell>
                    <TableCell>{formatDuration(computeDurationSeconds(r))}</TableCell>
                    <TableCell>{formatFileSize(r.file_size)}</TableCell>
                    <TableCell><StatusChip status={r.recording_status} /></TableCell>
                    <TableCell align="right">
                      <Tooltip title={r.recording_status === 'COMPLETED' ? 'Watch' : 'Not available yet'}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={r.recording_status !== 'COMPLETED'}
                            onClick={() => handleWatch(r)}
                            aria-label="Watch recording"
                          >
                            <PlayCircleOutline fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={r.recording_status === 'COMPLETED' ? 'Download' : 'Not available yet'}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={r.recording_status !== 'COMPLETED'}
                            onClick={() => handleDownload(r)}
                            aria-label="Download recording"
                          >
                            <Download fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => handleDetails(r)} aria-label="View recording details">
                          <InfoOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!loading && !error && recordings.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2.5 }}>
            <Pagination
              count={pagination.totalPages || 1}
              page={page}
              onChange={(e, value) => setPage(value)}
              color="primary"
            />
          </Box>
        )}
      </Paper>

      {watchRecordingId && (
        <RecordingPlayerModal
          recordingId={watchRecordingId}
          open={Boolean(watchRecordingId)}
          onClose={() => setWatchRecordingId(null)}
        />
      )}

      <RecordingDetailsModal
        recording={detailsRecording}
        open={Boolean(detailsRecording)}
        onClose={() => setDetailsRecording(null)}
        onWatch={(recording) => { setDetailsRecording(null); handleWatch(recording); }}
        onDownload={handleDownload}
      />
    </Container>
  );
}
